import { z } from 'zod'
import type { Channel, ConversationState, MessageStatus, Space, StaffRole, TaskStage } from './enums'
import type { FieldDef, ObjectLayout, ObjectPermissions } from './fields'
import { TaskActionMore, type TaskDetailMore } from './tasks'
import { ViewFilter } from './crm/views'
import type { AutonomyCellTrust, ChangeSetTrust, InsightTrust, TestCaseView, TestSummaryView } from './trust'

/**
 * Console API contract: base path /api/v1, JSON, `Authorization: Bearer <Supabase access token>`.
 * Every action goes through the server and is audited (design 17.1).
 * Lists are always paged: `?limit=` (max 100) and `?cursor=`; responses are `Page<T>`.
 */
export interface Page<T> { items: T[]; next_cursor: string | null }
export interface ApiError { error: { code: string; message: string; details?: unknown } }

// ---------------------------------------------------------------------------
// Me
// ---------------------------------------------------------------------------
export interface MeView {
  staff: { id: string; name: string; email: string; role: StaffRole; on_duty: boolean }
  company: { id: string; slug: string; name: string; region: string; timezone: string; currency: string; industry_pack: string | null; logo_letter: string; color: string
    /** The company's logo (brand.logo_url, https), shown in the console header; the letter tile when there is none. */
    logo_url?: string | null }
  spaces: Space[]
}

// ---------------------------------------------------------------------------
// Shared view pieces
// ---------------------------------------------------------------------------
export interface PersonRef { id: string; name: string; organisation: string | null; flags: string[] }

export interface MessageView {
  id: string
  conversation_id: string
  channel: Channel | 'system'
  direction: 'inbound' | 'outbound' | 'internal'
  author_kind: 'customer' | 'ai' | 'staff' | 'system' | 'agent'
  author_name: string | null
  subject: string | null
  body_text: string
  status: MessageStatus
  confidence: number | null
  citations: { kind: string; id: string; title: string }[] | null
  /** `paused`: why a queued message is waiting (a kill switch); it goes when the pause is lifted. */
  policy: { checks?: { name: string; ok: boolean; detail?: string }[]; paused?: string | null } | null
  created_at: string
  sent_at: string | null
  delivered_at: string | null
  /** A scheduled AI reply goes out at this time unless a person stops it (Level 2 veto window). */
  send_after: string | null
  /**
   * Files that came with the message (design 6.1, 18.2): stored ones with their scan result; ones intake only
   * listed have no id, scan_status 'skipped' and downloadable false.
   */
  attachments?: import('./email').AttachmentView[]
  /** A reply a person sent from outside takeit4me (a dual run), read back in: where it came from. Never sent by us. */
  sent_outside?: { source: string } | null
  /** An AI message a person reported as a critical error (design 19 "report a mistake"). */
  mistake?: { reason: string; by: string | null; at: string } | null
}

/** A call on a timeline (design 11.2 step 17, 18.2): summary, transcript and recording. */
export interface CallTimelineView {
  id: string; state: string; duration_s: number | null; summary: string | null; from: string | null
  direction?: 'inbound' | 'outbound'
  transport?: 'sip' | 'web'
  verified?: boolean
  /** The recording, when one was kept (played through GET /calls/:id/recording). */
  recording?: { available: boolean; status: string; disclosed: boolean; expires_at: string | null } | null
  transcript?: { at: string; speaker: string; text: string }[]
}

/** One timeline item for a person, organisation or conversation (design 7.4). */
export type TimelineItem =
  | { kind: 'message'; at: string; message: MessageView }
  | { kind: 'call'; at: string; call: CallTimelineView }
  | { kind: 'task'; at: string; task: { id: string; ref: string; title: string; stage: TaskStage } }
  | { kind: 'task_event'; at: string; task_ref: string; event: { id?: string; kind: string; actor: string; text: string } }
  | { kind: 'change'; at: string; change: { id: string; entity: string; summary: string; actor: string; undoable: boolean; undone: boolean; fields?: ChangeField[] } }
  | { kind: 'note'; at: string; note: { id: string; author: string; body: string } }
  | { kind: 'payment'; at: string; payment: { id: string; kind: string; amount_minor: number; currency: string; status: string } }
  | { kind: 'invoice'; at: string; invoice: { id: string; number: string; status: string; total_minor: number; currency: string; po_number: string | null } }

export interface DecisionView {
  question_key: string
  label: string
  decided_by: 'rule' | 'jev' | 'llm' | 'none'
  answer: string | null
  probability: number | null
  fired: boolean
  fallback_reason: string | null
}

// ---------------------------------------------------------------------------
// Today (screen 1)
// ---------------------------------------------------------------------------
/** One field a change touched: its value before and after (shown as text; "•••" when the viewer may not see it). */
export interface ChangeField { field: string; before: string | null; after: string | null }
/** A row on the Changes page (GET /crm/changes, audit history across customers and records). */
export interface ChangeLogItem {
  id: string; at: string; entity: string; entity_id: string; object: string; label: string | null
  link: { object: string; id: string } | null; op: string; actor_kind: string; actor: string; reason: string | null; summary: string
  fields: ChangeField[]; undoable: boolean; undone: boolean; undo_of: string | null
}

export interface TodayView {
  headline: { key: string; label: string; value: string; delta: string | null; definition: string }[]
  needs_you: { kind: 'exception' | 'task'; id: string; ref: string; title: string; reason: string; priority: number; deadline_at: string | null; channel: Channel | null }[]
  /** `url`: the item the alarm is about (a conversation, a desk item, a run), when it is about one. `detail`: since when,
   * how many, and example items to look at (outcome and intake alarms). */
  alarms: { id: string; kind: string; subject: string; message: string; severity: string; status: 'open' | 'acknowledged'; opened_at: string; url?: string | null
    detail?: { since?: string | null; affected?: number; examples?: { kind: string; id: string; url: string; label?: string }[] } }[]
  autonomy_changes: { id: string; subject: string; channel: string; from_level: number; to_level: number; kind: string; reason: string; status: string }[]
  live: { calls: number; chats: number }
}

// ---------------------------------------------------------------------------
// Exceptions (screen 2)
// ---------------------------------------------------------------------------
export interface ExceptionListItem {
  id: string
  ref: string
  reason_code: string
  reason: string
  priority: number
  deadline_at: string | null
  status: 'open' | 'held' | 'resolved' | 'cancelled'
  held_by: string | null
  person: PersonRef | null
  channel: Channel | null
  preview: string
  created_at: string
  /** The holder's staff id ("Mine" matches on ids, not names). */
  held_by_id?: string | null
  /** Who routing (or a colleague) gave this to, and who else it was offered to (design 12.5). */
  assigned_to?: { id: string; name: string } | null
  offered_to?: string[]
  route_note?: string | null
  /** Needs a person to approve something (over a limit, or the autonomy level): the Approvals tab. */
  approval?: boolean
}

export type PromiseKind = 'person_reply' | 'callback' | 'send_item' | 'refund' | 'follow_up' | 'other'
export interface ExceptionDetail extends ExceptionListItem {
  conversation: { id: string; ref: string; subject: string | null; state: ConversationState; request_type: string | null } | null
  task: { id: string; ref: string; title: string; stage: TaskStage } | null
  timeline: TimelineItem[]
  draft: MessageView | null
  recommendation: { summary: string; actions: { action: string; detail: string; amount_minor?: number }[] } | null
  decisions: DecisionView[]
  /** For a 'promise_due' item: the promise it is about. */
  promise_id: string | null
  /**
   * For an 'unidentified_sender' item (a rescued form or relayed email nobody could read the sender of): the address
   * it came through, and the customer's address the AI read, if any (verbatim: written whole in the email).
   */
  identify?: { via: string; suggestion: { email: string; name: string | null; evidence: string | null; verbatim: boolean } | null }
  /** The customer panel (design 18.2): since when, bookings, spend, and how they seem from their messages. */
  customer?: { since: string | null; bookings: number; spend_minor: number; currency: string | null; satisfaction: 'happy' | 'mixed' | 'unhappy' | null; satisfaction_basis: string | null } | null
  context: {
    facts: { fact: string; source: string; confidence: number }[]
    promises: { id: string; text: string; kind: PromiseKind; due_at: string; status: 'open' | 'kept' | 'broken' | 'cancelled'; closed_at: string | null; note: string | null
      /** From the commitments check: how to keep it, what happens if not, and whether the reply gave no time. */
      instructions?: string | null; consequence?: string | null; key_phrase?: string | null; due_estimated?: boolean }[]
    ai_actions: { id: string; summary: string; undoable: boolean }[]
    records: { id: string; object: string; title: string; data: Record<string, unknown> }[]
  }
}

export const ExceptionAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('hold'), over_colleague: z.boolean().optional() }),
  z.object({ action: z.literal('release') }),
  // expected_hash pins the approval to the draft the reviewer saw; confirm_newer acknowledges the customer wrote again;
  // undo_seconds holds the send that long so it can be undone (the console sends 10).
  // over_colleague: the reviewer saw that a colleague has the item open and chose to act anyway.
  z.object({ action: z.literal('approve'), execute_actions: z.boolean().default(true), expected_hash: z.string().optional(), confirm_newer: z.boolean().optional(), undo_seconds: z.number().int().min(0).max(30).optional(), over_colleague: z.boolean().optional() }),
  z.object({ action: z.literal('instruct'), instruction: z.string().min(3), over_colleague: z.boolean().optional() }),
  // A reason is asked for a substantial change; a wording fix (a minor edit) needs none.
  z.object({ action: z.literal('edit'), text: z.string().min(1), reason: z.string().optional(), expected_hash: z.string().optional(), confirm_newer: z.boolean().optional(), undo_seconds: z.number().int().min(0).max(30).optional(), over_colleague: z.boolean().optional() }),
  // Save an edit (or restore an earlier version) as the current draft without sending it: it can then be
  // refined by instruction, passed to a colleague, or left for later.
  z.object({ action: z.literal('save_edit'), text: z.string().min(1), reason: z.string().optional(), expected_hash: z.string().optional(), over_colleague: z.boolean().optional() }),
  z.object({ action: z.literal('reject'), reason: z.string().min(1), over_colleague: z.boolean().optional() }),
  z.object({ action: z.literal('take_over'), over_colleague: z.boolean().optional() }),
  z.object({ action: z.literal('hand_back'), note: z.string().optional() }),
  // Nothing needs saying (the customer only said thanks, or it was dealt with elsewhere): close without a reply.
  z.object({ action: z.literal('no_reply_needed'), reason: z.string().min(1), over_colleague: z.boolean().optional() }),
  // A promise was kept outside takeit4me (a call made, an item posted), or no longer applies.
  z.object({ action: z.literal('mark_promise'), promise_id: z.string().uuid(), outcome: z.enum(['kept', 'cancelled']), note: z.string().min(1) }),
  // A promise the reply made that is not tracked yet (a commitment the check could not read, or one made on a call).
  z.object({ action: z.literal('add_promise'), text: z.string().trim().min(3).max(300), kind: z.enum(['callback', 'send_item', 'refund', 'follow_up', 'other']), due_at: z.string().datetime({ offset: true }), instructions: z.string().trim().max(500).optional() }),
])
export type ExceptionAction = z.infer<typeof ExceptionAction>

// ---------------------------------------------------------------------------
// Conversations and Live (screen 3)
// ---------------------------------------------------------------------------
export interface ConversationListItem {
  id: string; ref: string; subject: string | null; state: ConversationState; request_type: string | null
  person: PersonRef | null; last_channel: Channel | null; last_message_at: string; preview: string; assigned_to: string | null
}
export interface ConversationDetail extends ConversationListItem {
  summary: string | null
  timeline: TimelineItem[]
  open_exceptions: { id: string; ref: string; reason: string }[]
  tasks: { id: string; ref: string; title: string; stage: TaskStage }[]
}
export const StaffReply = z.object({
  channel: z.enum(['email', 'sms', 'chat', 'whatsapp']).optional(), text: z.string().min(1), resolve: z.boolean().default(false),
  undo_seconds: z.number().int().min(0).max(30).optional(),
  /** What this reply commits to, tracked like the AI's promises (design 12.5). */
  promises: z.array(z.object({ text: z.string().min(3).max(300), due_at: z.iso.datetime({ offset: true }), kind: z.enum(['callback', 'send_item', 'refund', 'follow_up', 'other']).default('follow_up') })).max(5).optional(),
})

/** The customer's mood, from the classification of their latest message (core/mood.ts). */
export type LiveMood = 'calm' | 'neutral' | 'frustrated' | 'angry' | 'upset'
export interface LiveItem {
  kind: 'call' | 'chat'
  id: string
  conversation_id: string | null
  person: PersonRef | null
  from: string | null
  started_at: string
  request_type: string | null
  mood: LiveMood | null
  /** Who is with the customer now: the company's AI assistant, or a person. */
  handled_by: 'ai' | 'staff'
  /** The person's name when a colleague has it; null while the AI handles it or when only "a person" is known. */
  handler_name: string | null
  /** Calls: whether the recording notice was actually given on this call. */
  recording_disclosed?: boolean
  /** Calls: inbound or outbound (a callback or a journey call). */
  direction?: 'inbound' | 'outbound'
  /** Calls: a warm transfer in progress: who it is going to and whether they have the summary. */
  transfer?: { to: string | null; to_staff_id?: string | null; state: string; summary: string | null } | null
  /** Calls: how the caller was verified (booking code, postcode, a one-time code), if they were. */
  verified_by?: string | null
  /** What has been done in this call or chat, in a few words ("Booking moved; payment link sent"). */
  outcome?: string | null
  /** Outbound calls: what the call is about. */
  purpose?: string | null
  /** The company's assistant persona (brand.assistant_name), e.g. "Sam". */
  assistant: string
  /** Calls: ringing | active | transferring | ended | failed. Chats: with_ai | waiting (for a person) | with_person. */
  state: string
  /** Calls: 'sip' (a phone line) or 'web' (voice from the website). Null for chats. */
  transport: 'sip' | 'web' | null
  verified: boolean
  model: string | null
  voice: string | null
  transcript: { at: string; speaker: 'caller' | 'ai' | 'staff' | 'tool'; text: string }[]
}
/** A colleague a call can be handed to (GET /live/colleagues, Live space). */
export interface ColleagueView {
  id: string; name: string; role: StaffRole; skills: string[]; languages: string[]; on_duty: boolean
  /** The last digits of their number, to confirm the right line; never the full number. */
  phone_hint: string | null
  /** On duty with a number to ring. */
  available: boolean
  unavailable_reason: string | null
}
/** Live socket at /api/v1/live/ws?token=…: server sends LiveEvent JSON frames. */
export type LiveEvent =
  | { type: 'exception.created' | 'exception.updated'; id: string }
  | { type: 'message'; conversation_id: string; message_id: string }
  | { type: 'call.started' | 'call.updated' | 'call.ended'; id: string }
  | { type: 'call.transcript'; id: string; line: { at: string; speaker: string; text: string } }
  | { type: 'chat.updated'; id: string }
  | { type: 'task.updated'; id: string }
  | { type: 'alarm'; id: string }
  /** A notification for one staff member (staff_id), or for everyone on the desk (null). */
  | { type: 'notification'; id: string; staff_id: string | null }
  | { type: 'switches'; paused: boolean; scope: string }
  | { type: 'exception.assigned'; id: string; staff_id: string; by: string | null }

export const CallControl = z.discriminatedUnion('action', [
  z.object({ action: z.literal('whisper'), text: z.string().min(1) }),
  // Without staff_id the call goes to the company's transfer number; with it, to that on-duty colleague's phone.
  // Strict: a raw number is never accepted from the browser (the server resolves the colleague's own number).
  z.strictObject({ action: z.literal('take_over'), staff_id: z.uuid().optional(), to_me: z.boolean().optional() }),
  z.object({ action: z.literal('end') }),
  // Warm transfer (design 11.3): the specialist offered the call accepts it (it is put through to their own line) or passes.
  z.object({ action: z.literal('accept_transfer') }),
  z.object({ action: z.literal('decline_transfer'), reason: z.string().max(200).optional() }),
])
export type CallControl = z.infer<typeof CallControl>

// ---------------------------------------------------------------------------
// CRM (screen 5)
// ---------------------------------------------------------------------------
export interface ObjectTypeView {
  key: string; label: string; plural: string; icon: string | null; standard: boolean
  fields: FieldDef[]; title_field: string | null; count: number; ai_access: 'none' | 'read' | 'write'; version: number
  layout: ObjectLayout | null; permissions: ObjectPermissions | null
  /** Whether its records may link to a person and to an organisation. */
  links: { person: boolean; organisation: boolean }
  /** What the signed-in staff member may do with this object's records. */
  can: { read: boolean; write: boolean }
}
export interface PersonView {
  id: string; name: string; role: string | null; organisation: { id: string; name: string } | null
  flags: string[]; summary: string | null; language: string | null; fields: Record<string, unknown>
  site?: { id: string; name: string } | null
  identifiers: { id: string; kind: string; value: string; verified: boolean }[]
  consents: { purpose: string; channel: string; status: string; at: string }[]
  created_at: string; updated_at: string
  /** Personal-data (pii) field keys that hold a value the viewer may not see (services/crmPermissions.ts). */
  masked?: string[]
}
export interface OrganisationView {
  id: string; name: string; domain: string | null; flags: string[]; summary: string | null; fields: Record<string, unknown>
  people: { id: string; name: string; role: string | null }[]; created_at: string; updated_at: string
  /** Personal-data (pii) field keys that hold a value the viewer may not see. */
  masked?: string[]
}
/** Records that point at a record through one reference field (e.g. an order's lines). */
export interface LinkedRecordsView {
  object: string; label: string; plural: string; field: string; field_label: string; total: number; can_create: boolean; items: RecordView[]
}
export interface RecordView {
  id: string; object: string; title: string; data: Record<string, unknown>
  /** The linked person, with their flags (vulnerable, wrong number) so a record page shows them. */
  person: { id: string; name: string; flags?: string[] } | null; organisation: { id: string; name: string } | null
  created_at: string; updated_at: string
  /** Personal-data (pii) field keys that hold a value the viewer may not see. */
  masked?: string[]
}
export const PersonInput = z.object({
  name: z.string().min(1), role: z.string().nullish(), organisation_id: z.uuid().nullish(), language: z.string().nullish(),
  /** The customer's default site, used when the channel they write to has none (F 15.3-01). */
  site_id: z.uuid().nullish(),
  flags: z.array(z.string()).optional(), fields: z.record(z.string(), z.unknown()).optional(),
  emails: z.array(z.email()).optional(), phones: z.array(z.string()).optional(),
})
export const OrganisationInput = z.object({ name: z.string().min(1), domain: z.string().nullish(), flags: z.array(z.string()).optional(), fields: z.record(z.string(), z.unknown()).optional() })
export const RecordInput = z.object({ data: z.record(z.string(), z.unknown()), person_id: z.uuid().nullish(), organisation_id: z.uuid().nullish() })
export const AskInput = z.object({
  question: z.string().min(3), person_id: z.uuid().optional(), organisation_id: z.uuid().optional(),
  /** A question about one custom record (with `object`), or about a list: `object` alone, with the list's filters. */
  record_id: z.uuid().optional(), object: z.string().max(63).optional(),
  filters: z.array(ViewFilter).max(20).optional(),
})
export interface AskAnswer { answer: string; query: unknown; rows: Record<string, unknown>[] }
export const ImportCreate = z.object({ target: z.string(), filename: z.string().optional(), csv: z.string().min(1) })
export interface ImportView {
  id: string; target: string; status: string; rows_total: number; mapping: Record<string, string | null>
  preview: { create: number; update: number; duplicates: { row: number; match: string; reason: string }[]; errors: { row: number; message: string }[]; sample: Record<string, unknown>[] } | null
}

// ---------------------------------------------------------------------------
// Tasks (screen 6)
// ---------------------------------------------------------------------------
export interface TaskListItem {
  id: string; ref: string; title: string; kind: string; stage: TaskStage; route: string | null
  person: PersonRef | null; assignee: string | null; template: string | null; updated_at: string
  /** The consultant review gate: 'pending' while verified work waits for a reviewer (screen 6). */
  review_status: 'pending' | 'passed' | 'changes_requested' | null
  /** Delivery state for coding tasks (merging, merged, failed, rolled_back...). */
  delivery_status: string | null
}
export interface TaskDetail extends TaskListItem, TaskDetailMore {
  brief: { summary: string; details: string; acceptance: string[]; info: Record<string, string> }
  clarity: { ambiguity?: CheckResult; inconsistency?: CheckResult; incompleteness?: CheckResult; gaps?: { kind: string; question: string; answer?: string | null }[] }
  clarification_rounds: number
  assessment: {
    size: string; risk: string; cost_credits: number; covered_by_plan: boolean; reasons: string[]
    complexity?: string; minutes_estimate?: number; coverage?: 'free' | 'retainer' | 'quote'; quote_amount_minor?: number | null; risk_flags?: string[]
    retainer?: { record_id: string; hours_left: number } | null
  } | null
  quote: unknown
  work_mode: string | null; branch: string | null; preview_url: string | null; pull_request_url: string | null
  runs: { id: string; mode: string; agent: string; status: string; summary: string | null; checks: unknown; started_at: string; ended_at: string | null }[]
  events: { id: string; kind: string; actor_kind: string; actor: string | null; source: string | null; tool: string | null; text: string; data: unknown; at: string }[]
  thread: MessageView[]
  can_open_workspace: boolean
}
export interface CheckResult { passed: boolean; probability: number | null; decided_by: string; notes: string[] }
export const TaskAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('update_brief'), brief: z.object({ summary: z.string(), details: z.string(), acceptance: z.array(z.string()), info: z.record(z.string(), z.string()) }) }),
  z.object({ action: z.literal('set_route'), route: z.enum(['ai', 'ai_signoff', 'consultant']), reason: z.string().optional() }),
  z.object({ action: z.literal('assign'), staff_id: z.uuid() }),
  z.object({ action: z.literal('hand_to_ai') }),
  z.object({ action: z.literal('approve_result') }),
  z.object({ action: z.literal('request_changes'), note: z.string().min(1) }),
  z.object({ action: z.literal('cancel'), reason: z.string().min(1) }),
  z.object({ action: z.literal('comment'), text: z.string().min(1) }),
  z.object({ action: z.literal('quote'), credits: z.number().int().nonnegative(), note: z.string().optional() }),
  ...TaskActionMore,
])
export interface WorkspaceView { url: string; expires_at: string; instructions: string[] }
export interface AgentTokenView { token: string; signing_secret: string; expires_at: string; api_base: string; task_id: string | null }

// ---------------------------------------------------------------------------
// Studio, Improve, Settings
// ---------------------------------------------------------------------------
export interface QuestionView {
  key: string; label: string; layer: string; locked: boolean; type: string; instructions: string
  criteria: Record<string, string> | string[]; fires_on: string[]; threshold: number; unsure_low: number; unsure_high: number
  outcome: { action: string; reason: string; priority: number }; applies_to: string[]; enabled: boolean; version: number
  /** Cheap rules tried before any model (pattern, answer). */
  rules?: { pattern: string; answer: string }[]
  stats: { decisions_30d: number; fired_30d: number; jev_share: number; precision: number | null; recall: number | null; labels: number; cost_micros_30d: number }
}
export interface AutonomyCell extends AutonomyCellTrust {
  id: string; subject_kind: string; subject_key: string; channel: string; level: number; max_level: number; locked_reason: string | null
  evidence: { sent_unchanged_rate?: number; samples?: number; critical_errors_30d?: number; eligible_for?: number | null }
  pending: { id: string; to_level: number; reason: string } | null
  /** The request type's or template's name. */
  label?: string
  locked_by?: 'platform' | 'company' | null
  /** An open change set that raises this cell (waiting for test, approval or release). */
  pending_change_set?: { id: string; ref: string; to_level: number } | null
  /** On an "all channels" cell: open change sets that would give one channel its own level. */
  pending_overrides?: { channel: string; to_level: number; change_set: { id: string; ref: string } }[]
  /** Set when raising the level drafted a change set for a second admin instead of applying it. */
  change_set?: ChangeSetView
}
/**
 * A channel's own level for a request type that otherwise uses its "all channels" cell
 * (POST /studio/autonomy). The brain resolves a channel cell before the `any` cell (services/company.ts autonomyFor).
 */
export const AutonomyOverrideInput = z.object({
  subject_kind: z.enum(['request_type', 'task_template']), subject_key: z.string().min(1), channel: z.enum(['email', 'sms', 'chat', 'voice', 'whatsapp']),
  level: z.number().int().min(0).max(3), reason: z.string().min(1),
})
export interface AutonomyOverrideResult { cell: AutonomyCell | null; change_set: ChangeSetView | null }
export interface ChangeSetView extends ChangeSetTrust {
  id: string; ref: string; title: string; description: string | null; source: string; prompt: string | null
  changes: { entity: string; op: string; key: string; summary: string; before: unknown; after: unknown }[]
  status: string; requires_second_admin: boolean
  test_results: { passed: number; failed: number; cases: TestCaseView[]; summary?: TestSummaryView } | null
  created_by: string | null; approved_by: string | null; created_at: string; released_at: string | null
  /** The staff id behind `created_by`, so a creator can see that a second admin must approve. */
  created_by_id: string | null
}
/**
 * The Prompt Lab: a person picks past conversations to replay against a change set before it is
 * released: what was sent (by the AI on its own, or by a person), from the last N days, only the ones people
 * edited, one request type, from a pool of up to 100 ranked by how much the change touches them.
 */
export const LabFilters = z.object({
  days: z.number().int().min(1).max(90).default(30),
  source: z.enum(['any', 'sent', 'autonomous']).default('any'),
  edited_only: z.boolean().default(false),
  request_type: z.string().trim().min(1).max(80).optional(),
  limit: z.number().int().min(1).max(100).default(100),
})
export type LabFilters = z.infer<typeof LabFilters>
/** Up to 25 picked conversations at a time. */
export const LabRunInput = z.object({ message_ids: z.array(z.uuid()).min(1).max(25), filters: LabFilters.partial().optional() })
export interface LabCandidateView {
  message_id: string; conversation_id: string; at: string; subject: string | null; preview: string; request_type: string | null
  /** Who answered: the AI on its own, or a person (an AI draft they approved or edited, or their own reply). */
  sent_by: 'ai' | 'person'; edited: boolean
  /** How much the change touches it (the scout): higher first. */
  score: number; why: string
}
export interface LabDraftView { text: string; request_type: string | null; confidence: number; failed_checks: string[]; actions: string[] }
export interface LabResultView {
  message_id: string; conversation_id: string; customer_text: string; subject: string | null
  /** The AI's first draft for it, and what was finally sent (by a person, or the AI on its own). */
  ai_original: string | null; final_sent: string | null; final_by: 'ai' | 'person' | null
  before: LabDraftView | null; after: LabDraftView | null; error: string | null
  /** What changed besides the wording: the request type, held-by checks, proposed actions. */
  changes: string[]
  /** A judge's view of whether the new draft differs in substance (facts, offer, action, tone) or only in wording. */
  material: { material: boolean; reason: string } | null
}
export interface LabRunView {
  id: string; change_set_id: string; created_at: string; created_by: string | null; filters: Partial<LabFilters>
  results: LabResultView[]
  summary: { total: number; material: number; changed: number; failed: number }
}
export const AssistantInput = z.object({ prompt: z.string().min(5) })
export const ExplainInput = z.object({ question: z.string().min(3), conversation_id: z.uuid().optional(), message_id: z.uuid().optional() })
export interface InsightView extends InsightTrust {
  id: string; title: string; detail: string; impact: number; status: string; week: string
  evidence: { touch_id: string; kind: string; quote: string; staff: string | null; channel: string | null; at: string }[]
  change_set: ChangeSetView | null
}
export interface AnalyticsView {
  period: { from: string; to: string }
  measures: { key: string; label: string; value: number | null; unit: '%' | 's' | 'min' | 'count' | 'money' | 'rating'; target: string; definition: string; n?: number | null }[]
  weekly: { week: string; resolved_by_ai: number; conversations: number }[]
  by_channel: { channel: string; conversations: number; resolved_by_ai_rate: number | null; first_reply_s: number | null; cost_minor: number; rating?: number | null; ratings?: number }[]
  why_people: { reason_code: string; count: number }[]
  person_on_request: { median_s: number | null; honoured_rate: number | null }
}
export interface ChannelView {
  id: string; kind: Channel; name: string; address: string; provider: string; mode: 'off' | 'shadow' | 'live'
  config: Record<string, unknown>; last_inbound_at: string | null
  /** "not_answered": answered outside takeit4me, so left out of outcome and coverage alarms. */
  coverage?: 'answered' | 'not_answered'
  /** The site this number, address or widget belongs to: its conversations take this site (F 15.3-01). */
  site?: { id: string; name: string } | null
  /** `alarm`: the open silence or failed-send alarm on this channel, if any. */
  health: { ok: boolean; detail: string; alarm: { id: string; kind: string; severity: string; status: string; subject?: string | null } | null }
}
/** Kill switches (GET/PUT /switches). Platform and region are set by takeit4me and are read-only here. */
export type SwitchScope = 'company' | 'email' | 'sms' | 'chat' | 'voice' | 'whatsapp'
export interface SwitchState { paused: boolean; reason: string | null; set_by: string | null; set_at: string | null }
export interface SwitchesView {
  platform: { paused: boolean; reason: string | null }
  region: { paused: boolean; reason: string | null }
  company: SwitchState
  channels: Record<Channel, boolean>
  channel_details: Record<Channel, SwitchState>
}
export const SetSwitchInput = z.object({
  scope: z.enum(['company', 'email', 'sms', 'chat', 'voice', 'whatsapp']), paused: z.boolean(), reason: z.string().max(300).nullish(),
  /** On resume: hold the AI's replies written during the pause for review (the default), or false to release them. */
  hold_ai: z.boolean().optional(),
})
/** Outbound messages waiting: AI replies in their veto window, and anything held by a pause (GET /messages/pending). */
export interface PendingMessageView {
  id: string; conversation_id: string | null; channel: Channel; author_kind: 'ai' | 'staff' | 'system' | 'agent'; status: 'scheduled' | 'queued'
  send_after: string | null; paused: string | null; created_at: string; person_name: string | null; conversation_ref: string | null; preview: string
  /** Who has the conversation open, holding the send back (at most 30 minutes, then it goes to the desk). */
  held_up_by?: string | null
}
/** Channel health for Today (GET /today/channels): the same health, without provider configuration. */
export type ChannelHealthView = Omit<ChannelView, 'config'>
export const ChannelInput = z.object({
  kind: z.enum(['email', 'sms', 'chat', 'voice', 'whatsapp']), name: z.string().min(1), address: z.string().min(1), provider: z.string().min(1),
  config: z.record(z.string(), z.unknown()).default({}), mode: z.enum(['off', 'shadow', 'live']).default('shadow'),
  site_id: z.uuid().nullish(),
})
export const StaffInput = z.object({
  email: z.email(), name: z.string().min(1), role: z.enum(['admin', 'supervisor', 'specialist', 'consultant']),
  skills: z.array(z.string()).default([]), languages: z.array(z.string()).default(['en']), on_duty: z.boolean().default(false),
  own_subscription_allowed: z.boolean().default(false),
  /** Where live calls are put through to them: a phone number (stored as E.164) or a sip: URI. */
  phone: z.string().trim().max(200).nullish(),
})
